'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const industries = [
  'Ride Hailing',
  'Taxi Services',
  'Corporate Transport',
  'Logistics & Parcel Delivery',
  'Food & Grocery Delivery',
  'Hyperlocal Services',
  'EV Fleet Operations',
];

export default function CTABanner() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-on-scroll').forEach((el) => {
              el.classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <>
      {/* Industries We Serve */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="section-label block mb-3">Industries We Serve</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Built for{' '}
              <span className="italic font-light text-[#0066FF]">Multiple Industries.</span>
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {industries.map((industry) => (
              <span
                key={industry}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#FFFFFF] border border-[rgba(0, 102, 255,0.2)] text-sm font-medium text-[#0F172A] hover:bg-[rgba(0, 102, 255,0.06)] hover:border-[rgba(0, 102, 255,0.4)] transition-all cursor-default"
              >
                <span className="w-2 h-2 rounded-full bg-[#0066FF]" />
                {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Expansion Section */}
      <section className="py-20 bg-[#FFFFFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="section-label block mb-3">Expansion</span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight leading-tight mb-6">
                From Tamil Nadu to{' '}
                <span className="italic font-light text-[#0066FF]">Pan India.</span>
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-6">
                Starting with strong traction in South India, Movio is actively expanding across multiple states and preparing for national scale.
              </p>
              <p className="text-[#475569] text-lg leading-relaxed mb-8">
                Our vision is simple: <span className="font-semibold text-[#0F172A]">50+ Cities. Thousands of Operators.</span>
              </p>
              <div className="flex flex-wrap gap-3">
                {['Tamil Nadu', 'Kerala', 'Karnataka', 'Chhattisgarh'].map((state) => (
                  <span key={state} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[rgba(0, 102, 255,0.25)] text-sm font-medium text-[#0066FF]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF]" />
                    {state}
                  </span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '22+', label: 'Cities Operational', icon: 'MapPinIcon' },
                { value: '4', label: 'State Presence', icon: 'GlobeAltIcon' },
                { value: '50+', label: 'Target Cities', icon: 'RocketLaunchIcon' },
                { value: '1000s', label: 'Operators Vision', icon: 'UsersIcon' },
              ].map((stat) => (
                <div key={stat.label} className="bg-white rounded-2xl p-6 border border-[rgba(15,23,42,0.07)] shadow-card text-center">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: 'linear-gradient(135deg, #0066FF20, #075DDF10)' }}>
                    <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#0066FF]" />
                  </div>
                  <div className="font-display text-3xl font-semibold text-gradient mb-1">{stat.value}</div>
                  <div className="text-xs text-[#64748B] font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section ref={ref} className="py-24 bg-[#FFFFFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="animate-on-scroll stagger-1 relative rounded-3xl overflow-hidden p-12 md:p-16 text-center shadow-2xl border border-[#E2E8F0]"
style={{
  background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 40%, #EAF4FF 100%)"
}}         >
            {/* Grid overlay */}
            <div className="absolute inset-0 grid-lines opacity-5" />
            {/* Orb */}
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-20 pointer-events-none"
style={{
  background: 'radial-gradient(circle, #E2E8F0 0%, transparent 70%)',
  filter: 'blur(80px)'
}}            />

            <div className="relative z-10">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#CBD5E1] bg-white text-[11px] font-semibold tracking-widest text-[#0066FF] uppercase mb-6 shadow-sm">
                Ready to Scale?
              </span>
              <h2 className="font-display text-4xl md:text-6xl font-semibold text-[#0F172A] tracking-tight leading-tight mb-6">
                Ready to Digitize and Scale
                <br />
                <span className="italic font-light text-[#334155]">Your Business?</span>
              </h2>
              <p className="text-[#475569] text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
                Whether you're a taxi operator, logistics provider, delivery network, or regional entrepreneur—Movio gives you the technology to grow faster and compete smarter.
              </p>
              <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
                <Link
                  href="/demo-booking"
className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold bg-[#0066FF] text-white hover:bg-[#0052CC] transition-all duration-300 shadow-lg hover:shadow-xl"                >
                  Book a Demo
                  <Icon name="ArrowRightIcon" size={18} />
                </Link>
                <Link
                  href="/contact"
className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-[#0F172A] border border-[#CBD5E1] hover:bg-white hover:border-[#94A3B8] transition-all duration-300"                >
                  <Icon name="UserGroupIcon" size={16} />
                  Become a Partner
                </Link>
              </div>
              <p className="mt-8 text-[#64748B] text-sm">Book a Demo. Become a Partner. Build Your Future.</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}